import React, { useEffect, useMemo, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    InputAdornment,
    Typography,
    useTheme,
    CircularProgress,
} from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import SearchIcon from '@material-ui/icons/Search'
import {
    Container,
    EnhancedTableHead,
    HeadCell,
    LoadingButton,
    Order,
    RowData,
    CellData,
    stableSort,
} from '../../../components'
import { useStyles } from '../../../styles'
import { useOutreach } from '../../../hooks/Outreach/useOutreach'
import { SchoolPoolStatsRow } from '../../../models/Outreach/types'
import { typographyStyle } from '../../../theme/typographyStyle'
import { ProspectsTableFooter } from './ProspectsTableFooter'

const POOL_FETCH_LIMIT = 100

const POOL_HELP_PARAGRAPHS = [
    'This table lists schools at least one property serves. In sweep means a property serving that school has an unarchived leasing season, so scheduled target search includes it.',
    'Fetch 100 asks Pipe0 for one page of up to 100 profiles. 100 is a maximum, and a click can return zero new people. The next click starts at the next page. Both buttons stay disabled until that fetch finishes.',
    'AMAP starts over from the first page and keeps requesting pages until Crust has no more results. Each page spends Pipe0 credits, so a large school can be many searches. Both buttons stay disabled until it finishes.',
]

interface PoolTableRow extends RowData {
    displayName: CellData<string>
    schoolId: CellData<number>
    totalEnrollment: CellData<string>
    institutionType: CellData<string>
    propertiesServed: CellData<number>
    propertiesPrimary: CellData<number>
    inPoolSweep: CellData<string>
    totalProspects: CellData<number>
    searchedProspects: CellData<number>
    localProspects: CellData<number>
    lastSearchDate: CellData<string>
    source: CellData<SchoolPoolStatsRow>
}

function formatInstitutionType(degrees: number | null): string {
    if (degrees == null) return '—'
    switch (degrees) {
        case 1:
            return 'Certificate'
        case 2:
            return 'Associate'
        case 3:
            return 'Bachelor'
        case 4:
            return 'Graduate'
        default:
            return String(degrees)
    }
}

function formatEnrollment(total: number | null): string {
    if (total == null) return '—'
    return total.toLocaleString()
}

function formatLastSearch(iso: string | null): string {
    if (!iso) return '—'
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

function yesNo(value: boolean): string {
    return value ? 'Yes' : 'No'
}

function descendingComparator<T extends RowData>(
    a: T,
    b: T,
    orderBy: keyof T,
): number {
    if (b[orderBy].sortValue < a[orderBy].sortValue) {
        return -1
    }
    if (b[orderBy].sortValue > a[orderBy].sortValue) {
        return 1
    }
    return 0
}

function getComparator<T extends RowData>(
    order: Order,
    orderBy: keyof T,
): (a: T, b: T) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy)
}

function normalizeSearch(text: string): string {
    return text.trim().toLowerCase()
}

function schoolMatchesSearch(
    row: SchoolPoolStatsRow,
    queryRaw: string,
): boolean {
    const q = normalizeSearch(queryRaw)
    if (!q) return true
    return (
        normalizeSearch(row.display_name).includes(q) ||
        String(row.id).includes(q)
    )
}

function createPoolTableRow(row: SchoolPoolStatsRow): PoolTableRow {
    const lastLabel = formatLastSearch(row.last_search_date)
    const lastSort = row.last_search_date
        ? new Date(row.last_search_date).getTime()
        : 0
    return {
        displayName: {
            value: row.display_name,
            sortValue: row.display_name.toLowerCase(),
        },
        schoolId: {
            value: row.id,
            sortValue: row.id,
        },
        totalEnrollment: {
            value: formatEnrollment(row.fall_enrollment_total),
            sortValue: row.fall_enrollment_total ?? -1,
        },
        institutionType: {
            value: formatInstitutionType(row.degrees_awarded_highest),
            sortValue: row.degrees_awarded_highest ?? -1,
        },
        propertiesServed: {
            value: row.num_properties_served,
            sortValue: row.num_properties_served,
        },
        propertiesPrimary: {
            value: row.num_properties_primary,
            sortValue: row.num_properties_primary,
        },
        inPoolSweep: {
            value: yesNo(row.in_pool_sweep),
            sortValue: row.in_pool_sweep ? 1 : 0,
        },
        totalProspects: {
            value: row.total_prospects,
            sortValue: row.total_prospects,
        },
        searchedProspects: {
            value: row.searched_prospects,
            sortValue: row.searched_prospects,
        },
        localProspects: {
            value: row.local_prospects,
            sortValue: row.local_prospects,
        },
        lastSearchDate: {
            value: lastLabel,
            sortValue: lastSort,
        },
        source: {
            value: row,
            sortValue: row.display_name.toLowerCase(),
        },
    }
}

export const PoolTab: React.FC = () => {
    const theme = useTheme()
    const classes = useStyles()
    const {
        schoolPoolStats,
        isLoadingSchoolPoolStats,
        runSchoolPoolSearch,
        grabSchoolPool,
    } = useOutreach({ loadSchoolPool: true })

    const [order, setOrder] = useState<Order>('asc')
    const [orderBy, setOrderBy] = useState<keyof PoolTableRow>('displayName')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [searchQuery, setSearchQuery] = useState('')
    const [helpOpen, setHelpOpen] = useState(false)
    const [fetchingSchoolId, setFetchingSchoolId] = useState<number | null>(
        null,
    )
    const [amapSchool, setAmapSchool] = useState<{
        id: number
        name: string
    } | null>(null)
    const [grabbingSchoolId, setGrabbingSchoolId] = useState<number | null>(
        null,
    )

    const filteredRows = useMemo(
        () =>
            (schoolPoolStats ?? []).filter((row) =>
                schoolMatchesSearch(row, searchQuery),
            ),
        [schoolPoolStats, searchQuery],
    )

    useEffect(() => {
        setPage(0)
    }, [searchQuery])

    const headCellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.h6),
        fontWeight: 500,
    }

    const headCells: HeadCell<PoolTableRow>[] = [
        {
            disablePadding: false,
            align: 'left',
            id: 'displayName',
            label: 'School',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'schoolId',
            label: 'ID',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'totalEnrollment',
            label: 'Total enrollment',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'institutionType',
            label: 'Institution type',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'propertiesServed',
            label: 'Properties served',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'propertiesPrimary',
            label: 'Primary on',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'inPoolSweep',
            label: 'In sweep',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'totalProspects',
            label: 'Total prospects',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'searchedProspects',
            label: 'Searched (Pipe0)',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'localProspects',
            label: 'Local (internal)',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'lastSearchDate',
            label: 'Last pool search',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'source',
            label: 'Fetch',
            style: headCellStyle,
            sortable: false,
        },
    ]

    const tableRows = useMemo(
        () => filteredRows.map((row) => createPoolTableRow(row)),
        [filteredRows],
    )

    const sortedRows = useMemo(
        () => stableSort(tableRows, getComparator(order, orderBy)),
        [tableRows, order, orderBy],
    )

    const pagedRows = useMemo(
        () =>
            sortedRows.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage,
            ),
        [sortedRows, page, rowsPerPage],
    )

    const handleRequestSort = (
        _: React.MouseEvent<unknown>,
        property: keyof PoolTableRow,
    ) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const handleFetchProspects = async (schoolId: number) => {
        setFetchingSchoolId(schoolId)
        try {
            await runSchoolPoolSearch.mutateAsync({
                schoolId,
                limit: POOL_FETCH_LIMIT,
            })
        } finally {
            setFetchingSchoolId(null)
        }
    }

    const handleConfirmAmap = async () => {
        if (!amapSchool) return
        const schoolId = amapSchool.id
        setGrabbingSchoolId(schoolId)
        try {
            await grabSchoolPool.mutateAsync(schoolId)
            setAmapSchool(null)
        } finally {
            setGrabbingSchoolId(null)
        }
    }

    if (isLoadingSchoolPoolStats) {
        return (
            <Container
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.grey[200],
                }}
            >
                <CircularProgress />
            </Container>
        )
    }

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                backgroundColor: theme.palette.grey[200],
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
            }}
        >
            <Container
                style={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: theme.spacing(2),
                    overflow: 'hidden',
                }}
            >
                <Paper
                    elevation={1}
                    style={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Container
                        style={{
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: theme.spacing(1.5),
                            padding: theme.spacing(2, 2, 1),
                            width: '100%',
                        }}
                    >
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search by school name or ID…"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ width: 360, maxWidth: '100%' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon
                                            color="action"
                                            fontSize="small"
                                        />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <IconButton
                            size="small"
                            aria-label="About the prospect pool"
                            style={{ marginLeft: 'auto' }}
                            onClick={() => setHelpOpen(true)}
                        >
                            <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Container>
                    <TableContainer
                        style={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                            paddingLeft: theme.spacing(2),
                            paddingRight: theme.spacing(2),
                        }}
                    >
                        <Table
                            className={classes.table}
                            stickyHeader
                            aria-label="served school prospect pool"
                            style={{ width: '100%' }}
                        >
                            <EnhancedTableHead<PoolTableRow>
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                rowCount={tableRows.length}
                                headCells={headCells}
                            />
                            <TableBody>
                                {pagedRows.map((tableRow) => (
                                    <TableRow key={tableRow.schoolId.value}>
                                        <TableCell>
                                            {tableRow.displayName.value}
                                        </TableCell>
                                        <TableCell align="center">
                                            {tableRow.schoolId.value}
                                        </TableCell>
                                        <TableCell align="center">
                                            {tableRow.totalEnrollment.value}
                                        </TableCell>
                                        <TableCell align="center">
                                            {tableRow.institutionType.value}
                                        </TableCell>
                                        <TableCell align="center">
                                            {tableRow.propertiesServed.value}
                                        </TableCell>
                                        <TableCell align="center">
                                            {tableRow.propertiesPrimary.value}
                                        </TableCell>
                                        <TableCell align="center">
                                            {tableRow.inPoolSweep.value}
                                        </TableCell>
                                        <TableCell align="center">
                                            {tableRow.totalProspects.value}
                                        </TableCell>
                                        <TableCell align="center">
                                            {tableRow.searchedProspects.value}
                                        </TableCell>
                                        <TableCell align="center">
                                            {tableRow.localProspects.value}
                                        </TableCell>
                                        <TableCell align="center">
                                            {tableRow.lastSearchDate.value}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Container
                                                style={{
                                                    justifyContent: 'center',
                                                    gap: theme.spacing(1),
                                                }}
                                            >
                                                <LoadingButton
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    loading={
                                                        fetchingSchoolId ===
                                                            tableRow.schoolId
                                                                .value ||
                                                        tableRow.source.value
                                                            .pool_search_in_flight
                                                    }
                                                    onClick={() =>
                                                        void handleFetchProspects(
                                                            tableRow.schoolId
                                                                .value,
                                                        )
                                                    }
                                                >
                                                    100
                                                </LoadingButton>
                                                <LoadingButton
                                                    size="small"
                                                    variant="outlined"
                                                    loading={
                                                        grabbingSchoolId ===
                                                            tableRow.schoolId
                                                                .value ||
                                                        tableRow.source.value
                                                            .pool_search_in_flight
                                                    }
                                                    onClick={() =>
                                                        setAmapSchool({
                                                            id:
                                                                tableRow
                                                                    .schoolId
                                                                    .value,
                                                            name:
                                                                tableRow
                                                                    .displayName
                                                                    .value,
                                                        })
                                                    }
                                                >
                                                    AMAP
                                                </LoadingButton>
                                            </Container>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {pagedRows.length === 0 && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={headCells.length}
                                            align="center"
                                        >
                                            No served schools found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <ProspectsTableFooter
                        count={sortedRows.length}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onChangePage={(_, newPage) => setPage(newPage)}
                        onChangeRowsPerPage={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10))
                            setPage(0)
                        }}
                    />
                </Paper>
            </Container>

            <Dialog
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
                maxWidth="sm"
                fullWidth
                aria-labelledby="pool-help-title"
            >
                <DialogTitle id="pool-help-title">Prospect pool</DialogTitle>
                <DialogContent>
                    {POOL_HELP_PARAGRAPHS.map((paragraph) => (
                        <Typography
                            key={paragraph}
                            variant="body1"
                            style={{
                                fontSize: '1.125rem',
                                lineHeight: 1.6,
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            {paragraph}
                        </Typography>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => setHelpOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={amapSchool !== null}
                onClose={() => {
                    if (grabbingSchoolId === null) setAmapSchool(null)
                }}
                maxWidth="sm"
                fullWidth
                aria-labelledby="amap-confirm-title"
            >
                <DialogTitle id="amap-confirm-title">
                    Fetch AMAP
                    {amapSchool ? ` — ${amapSchool.name}` : ''}
                </DialogTitle>
                <DialogContent>
                    <Typography
                        variant="body1"
                        style={{
                            fontSize: '1.125rem',
                            lineHeight: 1.6,
                        }}
                    >
                        AMAP walks every remaining page of Crust results for
                        this school and tries to get as many profiles as
                        possible. Each page asks for up to 100 profiles and
                        spends Pipe0 credits. A large school can be many
                        searches.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setAmapSchool(null)}
                        disabled={grabbingSchoolId !== null}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        color="primary"
                        variant="contained"
                        loading={grabbingSchoolId !== null}
                        onClick={() => void handleConfirmAmap()}
                    >
                        Fetch AMAP
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Container>
    )
}
